/**
 * コラムアーカイブ: カテゴリは URL を変えず REST で一覧を差し替え（ページ全体の再読み込みなし）
 * 件数: PC 9 / SP 8
 */
const COLUMN_CARD_ARROW_SVG =
  '<svg viewBox="0 0 57 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M0.800781 5.7998H54.8008L49.8008 0.799805" stroke="white" stroke-width="1.6" stroke-linecap="round"/></svg>';

class ColumnArchive {
  constructor() {
    this.root = document.querySelector('.js-column-archive');
    if (!this.root || typeof sanokikoColumnArchive === 'undefined') {
      return;
    }

    this._reqId = 0;
    this._activeTermId = null;
    this._currentCategoryPage = 1;
    this._currentCategoryMaxPages = 1;

    this.pcPerPage = sanokikoColumnArchive.postsPerPagePc || 9;
    this.spPerPage = sanokikoColumnArchive.postsPerPageSp || 8;
    this.fallbackImage = sanokikoColumnArchive.fallbackImage || '';

    this.listEl = this.root.querySelector('.js-column-archive-list');
    this.anchorEl = document.getElementById('archive-anchor');
    this.pagingWrap = this.root.querySelector('.js-column-archive-paging');
    this.filters = this.root.querySelectorAll('.js-column-cat-filter');
    this.restUrl = sanokikoColumnArchive.restUrl;
    this.termNames = sanokikoColumnArchive.termNames || {};
    this.emptyMessage =
      sanokikoColumnArchive.emptyMessage || '該当するコラムはありません。';

    if (!this.listEl || !this.filters.length) {
      return;
    }

    this.initialListHtml = this.listEl.innerHTML;
    this.initialPagingHtml = this.pagingWrap ? this.pagingWrap.innerHTML : '';

    this._loadingOverlay = this._createLoadingOverlay();
    this.root.style.position = 'relative';
    this.root.appendChild(this._loadingOverlay);

    this._bind();
    this._bindPagingDelegation();
    this._bindViewportChange();
    this._applyUrlCatParam();
  }

  /**
   * 別ページから /column/?cat=slug で遷移した場合、該当カテゴリで絞り込む
   * （事例アーカイブ /works/?cat= と同仕様）
   */
  _applyUrlCatParam() {
    const catParam = new URLSearchParams(window.location.search).get('cat');
    if (!catParam) return;

    const slug = catParam.split(',')[0].trim();
    if (!slug) return;

    const btn = [...this.filters].find((b) => b.dataset.termSlug === slug);
    if (!btn) return;

    const termId = btn.dataset.termId;
    if (!termId) return;

    this._activateFilter(btn);
    this._loadCategory(termId, 1);
  }

  _createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('role', 'status');
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.75)',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.2s',
    });

    const spinner = document.createElement('span');
    Object.assign(spinner.style, {
      display: 'block',
      width: '4rem',
      height: '4rem',
      borderRadius: '50%',
      border: '.4rem solid #e0e0e0',
      borderTopColor: '#2A892C',
      animation: 'column-archive-spin 0.8s linear infinite',
    });
    overlay.appendChild(spinner);

    if (!document.getElementById('column-archive-spin-style')) {
      const style = document.createElement('style');
      style.id = 'column-archive-spin-style';
      style.textContent =
        '@keyframes column-archive-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    return overlay;
  }

  _showLoading() {
    this._loadingOverlay.style.opacity = '1';
    this._loadingOverlay.style.pointerEvents = 'auto';
  }

  _hideLoading() {
    this._loadingOverlay.style.opacity = '0';
    this._loadingOverlay.style.pointerEvents = 'none';
  }

  _getPerPage() {
    return window.matchMedia('(max-width: 767px)').matches
      ? this.spPerPage
      : this.pcPerPage;
  }

  _scrollToArchiveAnchor() {
    const el = this.anchorEl || this.listEl;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  _bind() {
    this.filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const termId = btn.dataset.termId;
        this._activateFilter(btn);
        if (!termId) {
          this._showAll();
        } else {
          this._loadCategory(termId, 1);
        }
      });
    });
  }

  _bindPagingDelegation() {
    if (!this.pagingWrap) return;
    this.pagingWrap.addEventListener('click', (e) => {
      if (this._activeTermId == null) return;
      const pageBtn = e.target.closest('.js-column-archive-page');
      if (pageBtn) {
        const p = parseInt(pageBtn.dataset.page, 10);
        if (!Number.isNaN(p)) {
          this._loadCategory(this._activeTermId, p);
        }
        return;
      }
      if (e.target.closest('.js-column-archive-prev')) {
        if (this._currentCategoryPage > 1) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage - 1);
        }
        return;
      }
      if (e.target.closest('.js-column-archive-next')) {
        if (this._currentCategoryPage < this._currentCategoryMaxPages) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage + 1);
        }
      }
    });
  }

  _bindViewportChange() {
    let prev = this._getPerPage();
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      const next = this._getPerPage();
      if (next === prev) return;
      prev = next;
      if (this._activeTermId != null) {
        this._loadCategory(this._activeTermId, 1);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
  }

  _activateFilter(activeBtn) {
    this.filters.forEach((b) => {
      const on = b === activeBtn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        b.setAttribute('aria-current', 'true');
      } else {
        b.removeAttribute('aria-current');
      }
    });
  }

  _showAll() {
    this._activeTermId = null;
    this._reqId += 1;
    this.listEl.innerHTML = this.initialListHtml;
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = this.initialPagingHtml;
      this.pagingWrap.hidden = false;
    }
  }

  _clearPaging() {
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = '';
      this.pagingWrap.hidden = true;
    }
  }

  _setEmpty() {
    this.listEl.innerHTML = `<li class="text-center text-[1.4rem] text-[#888888] py-[2rem]">${this.emptyMessage}</li>`;
    this._clearPaging();
    this._scrollToArchiveAnchor();
  }

  async _loadCategory(termId, page = 1) {
    const req = ++this._reqId;
    this._activeTermId = termId;
    this._showLoading();

    let result;
    try {
      result = await this._fetchPostsPage(termId, page);
    } catch (e) {
      if (req !== this._reqId) return;
      this._hideLoading();
      this._setEmpty();
      return;
    }

    if (req !== this._reqId) return;
    this._hideLoading();

    const { posts, totalPages } = result;
    this._currentCategoryPage = page;
    this._currentCategoryMaxPages = Math.max(1, totalPages);

    if (!posts.length) {
      this._setEmpty();
      return;
    }

    this.listEl.innerHTML = posts.map((p) => this._renderPost(p)).join('');

    if (this.pagingWrap) {
      this.pagingWrap.hidden = false;
      this.pagingWrap.innerHTML = this._renderPagingNav(
        page,
        this._currentCategoryMaxPages,
      );
    }

    this._scrollToArchiveAnchor();
  }

  async _fetchPostsPage(termId, page) {
    const perPage = this._getPerPage();
    const url = new URL(this.restUrl, window.location.origin);
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    url.searchParams.set('column_cat', String(termId));
    url.searchParams.set('_embed', '1');

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
    });

    if (!res.ok) {
      throw new Error('REST request failed');
    }

    const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '0', 10);
    const posts = await res.json();
    return {
      posts: Array.isArray(posts) ? posts : [],
      totalPages: Number.isFinite(totalPages) ? totalPages : 0,
    };
  }

  _buildPageItems(maxPages, paged) {
    if (maxPages <= 1) return [];
    const show = { 1: true, [maxPages]: true };
    const left = Math.max(2, paged - 2);
    const right = Math.min(maxPages - 1, paged + 2);
    for (let i = left; i <= right; i++) show[i] = true;
    const keys = Object.keys(show)
      .map(Number)
      .sort((a, b) => a - b);
    const items = [];
    let prevKey = 0;
    for (const p of keys) {
      if (prevKey && p - prevKey > 1) {
        items.push({ ellipsis: true });
      }
      items.push({ page: p });
      prevKey = p;
    }
    return items;
  }

  _renderPagingNav(paged, maxPages) {
    if (maxPages <= 1) return '';

    const clsNav =
      'flex justify-center items-center gap-[.6rem] [&_*]:font-outfit';
    const clsPrevNextBase =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-bold text-[1.4rem] duration-300 pc:hover:opacity-70';
    const clsBtn =
      'js-column-archive-page w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-white text-[1.8rem] duration-300 [background:_radial-gradient(94.38%_94.38%_at_19.25%_6.75%,_#3DA73F_0%,_#2A892C_100%)] border border-[#CCCCCC] pc:hover:opacity-70';
    const clsCurrent =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-[1.8rem] duration-300 bg-white [border:_.1rem_solid_#2A892C] text-[#2A892C]';
    const clsEllipsis =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] flex items-center justify-center text-[1.8rem] text-[#888888]';

    const svgPrev = `<span style="transform:scaleX(-1);display:flex;"><svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
    const svgNext = `<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;

    let prevBlock;
    if (paged <= 1) {
      prevBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgPrev}</span>`;
    } else {
      prevBlock = `<button type="button" class="${clsPrevNextBase} js-column-archive-prev bg-white text-[#2A892C]" aria-label="前のページへ">${svgPrev}</button>`;
    }

    let nextBlock;
    if (paged >= maxPages) {
      nextBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgNext}</span>`;
    } else {
      nextBlock = `<button type="button" class="${clsPrevNextBase} js-column-archive-next bg-white text-[#2A892C]" aria-label="次のページへ">${svgNext}</button>`;
    }

    const pageItems = this._buildPageItems(maxPages, paged);
    const middle = pageItems
      .map((item) => {
        if (item.ellipsis) {
          return `<span class="${clsEllipsis}" aria-hidden="true">…</span>`;
        }
        const num = item.page;
        if (num === paged) {
          return `<span class="${clsCurrent}" aria-current="page">${num}</span>`;
        }
        return `<button type="button" class="${clsBtn}" data-page="${num}">${num}</button>`;
      })
      .join('');

    return `<nav class="${clsNav}" aria-label="コラムのページ送り">${prevBlock}${middle}${nextBlock}</nav>`;
  }

  _formatDate(iso) {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }

  _decodeTitle(html) {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  }

  _escapeHtml(str) {
    const el = document.createElement('div');
    el.textContent = str;
    return el.innerHTML;
  }

  _safeHref(link) {
    return /^https?:\/\//i.test(link || '') ? link : '#';
  }

  _getFeatured(post) {
    const emb = post._embedded;
    if (emb && emb['wp:featuredmedia'] && emb['wp:featuredmedia'][0]) {
      const m = emb['wp:featuredmedia'][0];
      const url = m.source_url || this.fallbackImage;
      const alt =
        (m.alt_text && String(m.alt_text)) ||
        (post.title && post.title.rendered
          ? this._decodeTitle(post.title.rendered)
          : '');
      return { url, alt: this._escapeHtml(alt) };
    }
    const titleRaw =
      post.title && post.title.rendered ? post.title.rendered : '';
    return {
      url: this.fallbackImage,
      alt: this._escapeHtml(this._decodeTitle(titleRaw)),
    };
  }

  _renderPost(post) {
    const titleRaw = post.title && post.title.rendered ? post.title.rendered : '';
    const title = this._escapeHtml(this._decodeTitle(titleRaw));
    const date = this._formatDate(post.date);
    const catIds = post.column_cat || [];
    const catName =
      catIds.length > 0
        ? this._escapeHtml(this.termNames[String(catIds[0])] || '')
        : '';
    const href = this._safeHref(post.link);
    const feat = this._getFeatured(post);
    const imgUrl = this._escapeHtml(feat.url);
    const imgAlt = feat.alt;

    const categoryBlock =
      catName !== ''
        ? `<p class="
          c-works-card-category
          shrink-0
          text-[1.3rem]
          bg-[#2A892C]
          !text-white
          px-[1.1rem]
          py-0
          leading-[1.5]

          sp:text-[1.3rem]
        ">${catName}</p>`
        : '';

    return `<li>
<a href="${href}" class="
  c-works-card

  bg-white
  rounded-[.4rem]
  block
  overflow-hidden
  [box-shadow:_0_.2rem_.4rem_0_rgba(0,_0,_0,_0.1)]
">
  <figure class="
    c-works-card-figure
    overflow-hidden
    relative
  ">
    <img class="c-works-card-img" src="${imgUrl}" alt="${imgAlt}">
  </figure>
  <div class="
    c-works-card-body
    p-[1.6rem]
    pb-[2.3rem]
    relative
  ">
    <div class="c-works-card-body-inner">
      <div class="
        c-works-card-categories
        flex
        items-center
        gap-[1rem]
      ">
        <time>${date}</time>
        ${categoryBlock}
      </div>
      <h3 class="
        c-works-card-title
        text-[1.6rem]
        font-bold
        mt-[0.6rem]
        leading-[1.5]

        sp:text-[1.8rem]
      ">${title}</h3>
    </div>
    <div class="c-works-card-body-footer">
      <div class="
        c-works-card-arrow
        c-arrow-flow
        absolute
        bottom-[1.5rem]
        right-[0.8rem]
        w-[4.8rem]
      ">
        <div class="c-arrow-flow__track">
          <span class="c-arrow-flow__item c-arrow-flow__item--reverse min-w-[4.8rem] [&_svg]:w-[4.2rem] [&_svg]:h-auto [&_svg_path]:stroke-[#2A892C] sp:[&_svg]:w-[4.2rem]">${COLUMN_CARD_ARROW_SVG}</span>
          <span class="c-arrow-flow__item min-w-[4.8rem] [&_svg]:w-[4.2rem] [&_svg]:h-auto [&_svg_path]:stroke-[#2A892C] sp:[&_svg]:w-[4.2rem]">${COLUMN_CARD_ARROW_SVG}</span>
        </div>
      </div>
    </div>
  </div>
</a>
</li>`;
  }
}
